import { apiEndpoint, getZaplifySdk } from '@zaplify/sdk';
import { ProspectCollectionDto } from '@zaplify/services/prospect-collections/shared';
import { atom, useAtom } from 'jotai';
import { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';

export const prospectCollectionsAtom = atom<ProspectCollectionDto[]>([]);
export const loadingProspectCollectionsAtom = atom<boolean>(false);

export const useProspectCollections = () => {
    const [prospectCollections, setProspectCollections] = useAtom(prospectCollectionsAtom);
    const [loading, setLoading] = useAtom(loadingProspectCollectionsAtom);
    const msal = useMsal();
    const profilesSdk = getZaplifySdk(apiEndpoint, msal.instance).profiles;

    const fetchProspectCollections = async () => {
        if (loading) {
            return;
        }
        setLoading(true);
        const collections = await profilesSdk.prospectCollections.getUserProspectCollections();
        setProspectCollections(collections);
        setLoading(false);
    };

    const refetchProspectCollections = async () => {
        await fetchProspectCollections();
    };

    const archiveProspectCollection = async (collectionId: string) => {
        setProspectCollections((collections) => collections.filter((collection) => collection.id !== collectionId));
        await profilesSdk.prospectCollections.updateProspectCollection(collectionId, { isArchived: true });
        await fetchProspectCollections();
    };

    const updateProspectCollectionName = async (collectionId: string, name: string) => {
        await profilesSdk.prospectCollections.updateProspectCollection(collectionId, { name });
        await fetchProspectCollections();
    };

    const removeProspectsFromProspectCollection = async (collectionId: string, prospectIds: string[]) => {
        const collection = prospectCollections?.find((collection) => collection.id === collectionId);
        if (!collection) {
            return;
        }
        const updatedProspectIds = collection.prospectIds.filter((id) => !prospectIds.includes(id));
        await profilesSdk.prospectCollections.updateProspectCollection(collectionId, {
            prospectIds: updatedProspectIds,
        });
        await fetchProspectCollections();
    };

    const createProspectCollection = async (name: string) => {
        await profilesSdk.prospectCollections.createProspectCollection({ name });
        await fetchProspectCollections();
    };

    useEffect(() => {
        if (!loading) fetchProspectCollections();
    }, []);

    return {
        prospectCollections: prospectCollections || [],
        refetchProspectCollections,
        archiveProspectCollection,
        updateProspectCollectionName,
        removeProspectsFromProspectCollection,
        createProspectCollection,
    };
};
